import React from 'react';

/**
 * Editor for an active inventory session from history.
 * Allows adding products, updating counts, deleting items, and completing the session.
 */
const SessionEditor = ({
  selectedSession,
  sessionItems,
  loadingSession,
  addProductSearch, setAddProductSearch,
  searchResults,
  onAddProduct,
  onUpdateCount,
  onDeleteItem,
  onComplete,
  onBack,
  loading,
  readOnly = false,
  saveErrors = {}
}) => (
  <div className="card" style={{ fontSize: '12px' }}>
    <div className="card-header" style={{ padding: '0.4rem 0.75rem' }}>
      <div className="d-flex justify-content-between align-items-center">
        <div>
          <h5 className="card-title mb-0" style={{ fontSize: '13px', fontWeight: '600' }}>
            {readOnly ? 'Podglad' : 'Edycja'} inwentaryzacji: {selectedSession.document_number || `#${selectedSession.id}`}
          </h5>
          <small style={{ color: 'var(--text-muted)' }}>
            Rozpoczeto: {new Date(selectedSession.start_date).toLocaleString('pl-PL')}
            {selectedSession.end_date && ` | Zakonczona: ${new Date(selectedSession.end_date).toLocaleString('pl-PL')}`}
          </small>
        </div>
        <div className="d-flex gap-2">
          <button
            style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid var(--text-muted)', color: 'var(--text-muted)' }}
            onClick={onBack}
          >
            <i className="fas fa-arrow-left me-1"></i>Wroc
          </button>
          {!readOnly && (
            <button
              style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid var(--success)', color: 'var(--success)' }}
              onClick={onComplete}
              disabled={loading}
            >
              <i className="fas fa-check me-1"></i>Zakoncz inwentaryzacje
            </button>
          )}
        </div>
      </div>
    </div>

    <div className="card-body" style={{ padding: '0.5rem 0.75rem' }}>
      {/* Product search to add - only in edit mode */}
      {!readOnly && (
        <div style={{ marginBottom: '1rem', padding: '0.5rem', backgroundColor: 'var(--bg)', borderRadius: '4px' }}>
          <label style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', display: 'block', marginBottom: '0.25rem' }}>
            Dodaj produkt do inwentaryzacji
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              style={{ width: '100%', padding: '6px 10px', fontSize: '12px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }}
              placeholder="Szukaj po nazwie, kodzie EAN..."
              value={addProductSearch}
              onChange={(e) => setAddProductSearch(e.target.value)}
            />
            {searchResults.length > 0 && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0,
                backgroundColor: 'var(--card-bg)', border: '1px solid var(--border)',
                borderRadius: '0 0 4px 4px', maxHeight: '200px',
                overflowY: 'auto', zIndex: 1000, boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                {searchResults.map(product => (
                  <div
                    key={product.id}
                    style={{ padding: '0.5rem', borderBottom: '1px solid var(--border)', cursor: 'pointer', fontSize: '11px' }}
                    onClick={() => onAddProduct(product)}
                    onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--bg)'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--card-bg)'}
                  >
                    <strong>{product.name}</strong><br />
                    <small style={{ color: 'var(--text-muted)' }}>EAN: {product.barcode || 'brak'} | Kod: {product.product_code || 'brak'}</small>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Items table */}
      {loadingSession ? (
        <div className="text-center py-4">Ladowanie...</div>
      ) : (
        <div className="table-responsive">
          <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Produkt', 'Kod', 'Stan systemowy', 'Stan rzeczywisty', 'Roznica', ''].map(h => (
                  <th key={h} style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border)', textAlign: 'left', fontWeight: '600', color: 'var(--text-secondary)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sessionItems.map((item) => (
                <tr key={item.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '0.3rem 0.5rem' }}><strong>{item.product_name}</strong></td>
                  <td style={{ padding: '0.3rem 0.5rem' }}>
                    <code style={{ fontSize: '10px', backgroundColor: 'var(--border)', padding: '1px 4px', borderRadius: '2px' }}>{item.product_code || 'Brak'}</code>
                  </td>
                  <td style={{ padding: '0.3rem 0.5rem' }}>
                    <span style={{ fontSize: '10px', padding: '2px 6px', backgroundColor: 'var(--cyan)', color: '#fff' , borderRadius: '3px' }}>
                      {item.system_count} {item.unit || 'szt'}
                    </span>
                  </td>
                  <td style={{ padding: '0.3rem 0.5rem' }}>
                    {readOnly ? (
                      <span style={{ fontSize: '10px', padding: '2px 6px', backgroundColor: 'var(--info)', color: '#fff', borderRadius: '3px' }}>
                        {item.actual_count ?? 0} {item.unit || 'szt'}
                      </span>
                    ) : (
                      <>
                        <input
                          type="number"
                          style={{ width: '80px', fontSize: '11px', padding: '0.2rem 0.4rem', border: `1px solid ${saveErrors[item.id] ? 'var(--danger)' : 'var(--border)'}`, borderRadius: '3px', backgroundColor: saveErrors[item.id] ? 'rgba(220,53,69,0.08)' : undefined }}
                          value={item.actual_count ?? ''}
                          onChange={(e) => onUpdateCount(item.id, e.target.value)}
                          min="0"
                          placeholder="0"
                        />
                        {saveErrors[item.id] && (
                          <div style={{ fontSize: '9px', color: 'var(--danger)', marginTop: '2px' }} title={saveErrors[item.id]}>
                            <i className="fas fa-exclamation-triangle"></i> Niezapisane
                          </div>
                        )}
                      </>
                    )}
                  </td>
                  <td style={{ padding: '0.3rem 0.5rem' }}>
                    {item.actual_count !== undefined && item.actual_count !== null && (
                      <span style={{
                        fontSize: '10px', padding: '2px 6px', color: '#fff' , borderRadius: '3px',
                        backgroundColor: item.difference === 0 ? 'var(--success)' : item.difference > 0 ? 'var(--warning)' : 'var(--danger)'
                      }}>
                        {item.difference > 0 ? '+' : ''}{item.difference}
                      </span>
                    )}
                  </td>
                  {!readOnly && (
                    <td style={{ padding: '0.3rem 0.5rem' }}>
                      <button
                        style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--danger)', color: 'var(--danger)' , cursor: 'pointer' }}
                        onClick={() => onDeleteItem(item.id)}
                        title="Usun pozycje"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {sessionItems.length === 0 && !loadingSession && (
        <div className="text-center py-4 text-muted">
          <i className="fas fa-inbox fa-2x mb-2"></i>
          <p style={{ fontSize: '11px' }}>Brak pozycji w sesji. Dodaj produkty powyzej.</p>
        </div>
      )}
    </div>
  </div>
);

export default SessionEditor;
